/**
 * WHEN a session token wants refreshing - pure, no storage, no network.
 *
 * Tokens are 30-day JWTs (`auth_phone/jwt_signer.py`). Until
 * `POST /v1/auth/refresh/` existed they were minted at sign-in and never
 * again, so a browser signed in 30 days ago stopped working with nothing on
 * screen to say why. The web app now asks for a fresh one once more than HALF
 * the token's life has gone, which is what the signer's comment has always
 * promised: somebody who uses the app is never signed out on day 30.
 *
 * It is the phone's `refreshPlan.ts` for the same reason `emailAuth.ts` is
 * the phone's `email.ts`: one rule, two clients.
 *
 * Everything here reads the token's own `iat` and `exp` claims WITHOUT
 * verifying anything. A browser holds no key and must not; a decoded claim
 * only decides when to ASK the server, and the server's answer is the only
 * thing that says a session is dead.
 *
 * ── Clock skew ──────────────────────────────────────────────────────
 * This machine's clock and the server's disagree by seconds on a good day
 * and by hours on a bad one. So:
 *  - a token whose `exp` is in the past by THIS clock is not "expired", it is
 *    "ask the server": if the server still accepts it the refresh succeeds and
 *    nothing was lost; if not, the 401 is what ends the session;
 *  - a token that claims to be issued in the FUTURE (a clock set behind) has
 *    age zero, so it is not refreshed on every focus until the clocks agree.
 */

/** Tokens are 30 days; used when a token carries `exp` and no `iat`. */
export const DEFAULT_TOKEN_LIFE_SECONDS = 30 * 24 * 60 * 60;

/** An `iat` this far ahead of the clock is a clock problem, not a new token. */
export const SKEW_TOLERANCE_MS = 5 * 60 * 1000;

export type TokenTiming = {
  /** Seconds since the epoch, or null when the claim is absent. */
  iat: number | null;
  exp: number | null;
};

/** base64url -> text. The payload is JSON and the claims read here are
 *  numbers; a UTF-8 display name decodes as Latin-1, which `JSON.parse`
 *  accepts and nothing reads. */
function base64UrlDecode(input: string): string | null {
  const s = input.replace(/-/g, '+').replace(/_/g, '/');
  if (!s || /[^A-Za-z0-9+/=]/.test(s)) return null;
  try {
    return atob(s + '==='.slice((s.length + 3) % 4));
  } catch {
    return null;
  }
}

/** The `iat` and `exp` of a JWT, or null when it is not one. Never throws. */
export function tokenTiming(token: string | null | undefined): TokenTiming | null {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = base64UrlDecode(parts[1]);
  if (!json) return null;
  try {
    const claims = JSON.parse(json) as { iat?: unknown; exp?: unknown };
    const num = (v: unknown): number | null =>
      typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
    const exp = num(claims.exp);
    const iat = num(claims.iat);
    if (exp == null && iat == null) return null;
    return { iat, exp };
  } catch {
    return null;
  }
}

/** The moment (epoch ms) a token passes half its life, or null when its
 *  timing cannot be read. */
export function halfLifeAt(timing: TokenTiming | null): number | null {
  if (!timing || timing.exp == null) return null;
  const iat = timing.iat ?? timing.exp - DEFAULT_TOKEN_LIFE_SECONDS;
  const life = Math.max(0, timing.exp - iat);
  return (iat + life / 2) * 1000;
}

export type Freshness =
  /** Less than half its life gone: leave it alone. */
  | 'fresh'
  /** More than half gone: refresh it. */
  | 'due'
  /** Past `exp` by this clock: ask the server anyway (skew). */
  | 'expired'
  /** Not a JWT we can read: never refreshed proactively. */
  | 'unknown';

export function freshness(timing: TokenTiming | null, nowMs: number): Freshness {
  const half = halfLifeAt(timing);
  if (!timing || half == null || timing.exp == null) return 'unknown';
  if (nowMs >= timing.exp * 1000) return 'expired';
  // A token from the future has age zero: never "due" because of skew.
  if (timing.iat != null && timing.iat * 1000 > nowMs + SKEW_TOLERANCE_MS) return 'fresh';
  return nowMs >= half ? 'due' : 'fresh';
}

/** Whether to send a refresh now: due, or past `exp` (the server decides). */
export function refreshWanted(token: string | null | undefined, nowMs: number): boolean {
  const f = freshness(tokenTiming(token), nowMs);
  return f === 'due' || f === 'expired';
}

// ── backing off ───────────────────────────────────────────────────────

/** Waits after consecutive NETWORK failures, then holds at the last. A
 *  focus event is cheap and frequent; a refresh that cannot reach the
 *  server must not be retried on every one of them. */
export const BACKOFF_STEPS_MS = [30_000, 2 * 60_000, 10 * 60_000, 30 * 60_000];

/** How long to stay quiet after `failures` consecutive network failures. */
export function backoffMs(failures: number): number {
  if (failures <= 0) return 0;
  return BACKOFF_STEPS_MS[Math.min(failures, BACKOFF_STEPS_MS.length) - 1];
}

// ── what a failed refresh means ──────────────────────────────────────

export type RefreshFailure =
  /** 401: expired, revoked or deleted. The only answer that ends a session. */
  | 'unauthorized'
  /** 403 `refresh_unavailable` (a device-link token, a host-issued one) or a
   *  404 (a server without the endpoint): it will answer the same next time. */
  | 'unavailable'
  /** Offline, a timeout, a 5xx: says nothing about the session. */
  | 'network';

/** Classify a refresh response status (`0` for no response at all). */
export function classifyRefreshStatus(status: number): RefreshFailure {
  if (status === 401) return 'unauthorized';
  if (status === 403 || status === 404) return 'unavailable';
  return 'network';
}
